// lib/dinerAccount/rebuildCart.ts
//
// Reconstruye el carrito de un pedido viejo contra el menú de HOY. Puro: sin IO,
// sin React. Reglas:
//   - precio y nombre del menú ACTUAL (effectiveItemCardPrice / effectiveUnitPrice,
//     exactamente como ItemModal y FeaturedSection); jamás del snapshot.
//   - lo que no existe, está agotado, fuera de horario, o cuyo modificador ya no
//     existe, se SALTA entero y se reporta. Un plato a medias es peor que ninguno:
//     un plato con un modificador que ya no existe se cobraría mal o fallaría al
//     re-preciarse en el servidor.
//   - pizza por zonas (zone_layout ≠ 'whole') fuera en v1: order_item_modifiers
//     no guarda zona ni cantidad por modificador.
//   - súper online: productos por peso o con variantes fuera (no se sabe qué peso
//     ni qué variante repetir); solo existen con online_retail_v2.
import type { CartItem } from "@/components/microsite/types"
import { effectiveItemCardPrice, effectiveUnitPrice, type PricingConfig } from "@/lib/utils/pricing"
import { isItemAvailableNow } from "@/lib/menu/availability"
import type { ReorderLine } from "./reorderTypes"
import { applyRulesToLineModifiers } from "@/lib/menu/includedSelectionsCart"
import { needsRetailPicker, type RetailPickerFacts } from "@/lib/microsite/retailLine"

export type MenuItemLike = {
  id: string
  name: string
  price: number
  is_available: boolean | null
  available_from?: string | null
  available_until?: string | null
  available_days?: number[] | null
  item_modifier_groups?: Array<{
    modifier_groups: {
      id: string
      /** Selecciones incluidas (el mixto). Ausentes = sin regla. */
      included_selections?: number | null
      extra_selection_price?: number | null
      included_shared_portion?: boolean | null
      modifier_options: Array<{ id: string; name: string; price_delta: number; is_available: boolean | null; uses_all_included?: boolean | null }>
    } | null
  }>
  /** Súper online: weight_unit / variants solo llegan con online_retail_v2. */
} & RetailPickerFacts

export function rebuildCartFromLines(
  lines: ReorderLine[],
  items: MenuItemLike[],
  cfg: PricingConfig,
  timeZone: string,
  now: Date = new Date(),
): { cart: CartItem[]; skipped: string[] } {
  const byId = new Map(items.map((i) => [i.id, i]))
  const cart: CartItem[] = []
  const skipped: string[] = []

  for (const line of lines) {
    const item = line.itemId ? byId.get(line.itemId) : undefined
    if (!item || item.is_available === false || line.zoneLayout !== "whole") { skipped.push(line.name); continue }
    // Súper online: el pedido viejo no le dice al carrito qué peso ni qué variante
    // eligió, así que la línea se salta entera (como un plato a medias). Solo con
    // online_retail_v2 (la fila trae weight_unit / variants): sin la bandera, main.
    if (needsRetailPicker(item)) { skipped.push(line.name); continue }
    if (!isItemAvailableNow({ available_from: item.available_from, available_until: item.available_until, available_days: item.available_days }, timeZone, now)) {
      skipped.push(line.name); continue
    }

    const options = new Map<string, { groupId: string; name: string; price_delta: number; is_available: boolean | null }>()
    for (const g of item.item_modifier_groups ?? []) {
      const mg = g.modifier_groups
      if (!mg) continue
      for (const o of mg.modifier_options) options.set(o.id, { groupId: mg.id, name: o.name, price_delta: o.price_delta, is_available: o.is_available })
    }

    const modifiers: CartItem["modifiers"] = []
    let broken = false
    for (const m of line.modifiers) {
      const opt = m.modifier_option_id ? options.get(m.modifier_option_id) : undefined
      if (!opt || opt.is_available === false) { broken = true; break }
      modifiers.push({
        modifier_group_id: opt.groupId,
        modifier_option_id: m.modifier_option_id!,
        option_name: opt.name,
        price_delta: effectiveUnitPrice(opt.price_delta, cfg),
        quantity: 1,
      })
    }
    if (broken) { skipped.push(line.name); continue }

    // Selecciones incluidas (el mixto): la regla de HOY decide qué va incluido y qué
    // se cobra, igual que si el plato se armara de nuevo en ItemModal (el menú web
    // guarda el precio efectivo). El orden es el del pedido viejo.
    const ruledModifiers = applyRulesToLineModifiers(
      modifiers,
      (item.item_modifier_groups ?? []).map((g) => g.modifier_groups).filter((g): g is NonNullable<typeof g> => !!g),
      (raw) => effectiveUnitPrice(raw, cfg),
    )

    cart.push({
      cartId: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      itemId: item.id,
      name: item.name,
      price: effectiveItemCardPrice(item.price, cfg),
      quantity: line.quantity,
      modifiers: ruledModifiers,
      ...(line.specialInstructions ? { specialInstructions: line.specialInstructions } : {}),
    })
  }
  return { cart, skipped }
}
